import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { PaymentsService } from '../../services/payments.service';
import { PrintService } from '../../services/print.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';

export interface VendorRemitPreviewTabRequest {
	label: string;
	supplier_id: string;
	context: Record<string, unknown>;
	allocations: Array<{ trans_id: number; amount: number }>;
}

export interface VendorRemitPreviewModalData {
	title: string;
	tabs: VendorRemitPreviewTabRequest[];
	/** Bypass the live request — render this response directly (single-tab only). */
	prerendered?: VendorRemitPreviewResponse;
	/** Override the export file stem (e.g. 'brand_statement'); defaults to 'remit'. */
	exportFilenameStem?: string;
	/** Override the export document title used in the PDF header; defaults to the response subject. */
	exportTitle?: string;
	/** Render the exported PDF in landscape (e.g. wide breakdown tables); defaults to portrait. */
	exportLandscape?: boolean;
	/** Suppress the print wrapper's centered title/subtitle (the document already has its own header). */
	suppressPrintHeader?: boolean;
}

export interface VendorRemitPreviewResponse {
	success: boolean;
	message?: string;
	html?: string;
	warnings?: string[];
	recipient_email?: string;
	subject?: string;
}

@Component({
	selector: 'app-vendor-remit-preview-modal',
	templateUrl: './vendor-remit-preview-modal.component.html',
	styleUrls: ['./vendor-remit-preview-modal.component.scss']
})
export class VendorRemitPreviewModalComponent implements OnInit {
	loading = true;
	exporting = false;
	/** Active tab when there are multiple results (drives which one gets exported). */
	activeTabIndex = 0;
	/** One entry per tab; trustedHtml null if no body */
	results: Array<{
		label: string;
		res: VendorRemitPreviewResponse;
		trustedHtml: SafeHtml | null;
	}> = [];

	constructor(
		public dialogRef: MatDialogRef<VendorRemitPreviewModalComponent>,
		@Inject(MAT_DIALOG_DATA) public data: VendorRemitPreviewModalData,
		private paymentsService: PaymentsService,
		private printService: PrintService,
		private globalSearchService: GlobalSearchService,
		private sanitizer: DomSanitizer
	) {}

	ngOnInit(): void {
		if (!this.data.tabs || this.data.tabs.length === 0) {
			this.loading = false;
			return;
		}
		// Skip the live request when we already have rendered HTML
		// (e.g. View Remit on an existing payment, where the server already
		// produced the full preview by payment_id).
		if (this.data.prerendered && this.data.tabs.length === 1) {
			this.loading = false;
			const res = this.data.prerendered;
			const cleanHtml = res.success && res.html ? this.scrubPreviewOnlyNotices(res.html) : '';
			const trusted = cleanHtml
				? this.sanitizer.bypassSecurityTrustHtml(cleanHtml)
				: null;
			this.results = [{
				label: this.data.tabs[0].label,
				res: { ...res, warnings: this.filterRealWarnings(res.warnings) },
				trustedHtml: trusted,
			}];
			return;
		}
		const reqs = this.data.tabs.map((t) =>
			this.paymentsService.previewVendorRemittanceLive({
				supplier_id: t.supplier_id,
				context: t.context,
				allocations: t.allocations
			}).pipe(
				catchError(() =>
					of({
						success: false,
						warnings: ['Could not load preview.']
					} as VendorRemitPreviewResponse)
				)
			)
		);
		forkJoin(reqs).subscribe((responses: VendorRemitPreviewResponse[]) => {
			this.loading = false;
			this.results = this.data.tabs.map((t, i) => {
				const res = responses[i] || { success: false, warnings: ['Missing response'] };
				const cleanHtml = res.success && res.html ? this.scrubPreviewOnlyNotices(res.html) : '';
				const trusted = cleanHtml
					? this.sanitizer.bypassSecurityTrustHtml(cleanHtml)
					: null;
				return {
					label: t.label,
					res: { ...res, warnings: this.filterRealWarnings(res.warnings) },
					trustedHtml: trusted,
				};
			});
		});
	}

	/** Index of the result currently visible to the user (defaults to 0 in single-tab mode). */
	private currentResultIndex(): number {
		if (this.results.length <= 1) { return 0; }
		return Math.min(Math.max(this.activeTabIndex, 0), this.results.length - 1);
	}

	private currentExportContext(): { html: string; subject: string; subRequest: VendorRemitPreviewTabRequest } | null {
		const i = this.currentResultIndex();
		const row = this.results[i];
		const req = this.data.tabs[i];
		if (!row?.res?.html) { return null; }
		return {
			html: this.scrubPreviewOnlyNotices(row.res.html),
			subject: this.data.exportTitle || row.res.subject || 'Remittance Advice',
			subRequest: req,
		};
	}

	/**
	 * Drop email-config admonitions from the warnings list shown above the
	 * iframe — they describe the email-sending setup, not the remit itself.
	 * Real warnings (e.g. "Could not load preview", missing vendor) pass through.
	 */
	private filterRealWarnings(warnings: string[] | undefined): string[] {
		if (!Array.isArray(warnings)) { return []; }
		const NOTICES = [
			/Remittance email is turned off/i,
			/When enabled, this message will be sent to/i,
			/has no valid Supplier Email/i,
		];
		return warnings.filter((w) => !NOTICES.some((p) => p.test(String(w || ''))));
	}

	/**
	 * Strip in-preview admonitions (e.g. "Remittance email is turned off…",
	 * "When enabled, this message will be sent to…") so the rendered iframe and
	 * exported PDF/XLSX contain only the remit document itself.
	 */
	private scrubPreviewOnlyNotices(html: string): string {
		// An element is a "notice banner" if its OWN text starts with one of
		// these phrases. We don't care what follows — the whole element goes.
		const STARTS = /^(Remittance email is turned off|When enabled, this message will be sent to|This supplier has no valid Supplier Email|Sample preview only)/i;

		try {
			const doc = new DOMParser().parseFromString(html, 'text/html');
			const body = doc.body;
			if (!body) { return html; }

			const all = Array.from(body.querySelectorAll<HTMLElement>('*'));
			const toRemove: Element[] = [];
			for (const el of all) {
				const txt = (el.textContent || '').trim();
				if (!txt || !STARTS.test(txt)) { continue; }
				// Skip if this is a big container that happens to start with the
				// notice text (e.g. the outer wrapper when the banner is its first
				// child). The banner itself is typically <300 chars.
				if (txt.length > 320) { continue; }
				toRemove.push(el);
			}

			// Remove without affecting nested removals — sort by depth so we drop
			// the outermost banner first; inner elements would already be gone.
			toRemove.sort((a, b) => this.domDepth(a) - this.domDepth(b));
			const removed = new Set<Element>();
			for (const el of toRemove) {
				// Skip if any ancestor is already in our removal set.
				let p: Element | null = el.parentElement;
				let nested = false;
				while (p) {
					if (removed.has(p)) { nested = true; break; }
					p = p.parentElement;
				}
				if (nested) { continue; }
				removed.add(el);
				el.remove();
			}
			return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
		} catch {
			return html;
		}
	}

	private domDepth(el: Element): number {
		let d = 0;
		let p: Element | null = el.parentElement;
		while (p) { d++; p = p.parentElement; }
		return d;
	}

	private exportFilenameStem(): string {
		const i = this.currentResultIndex();
		const req = this.data.tabs[i];
		const supplier = (req?.supplier_id || 'vendor').toString().replace(/[^a-zA-Z0-9_-]/g, '_');
		const date = new Date().toISOString().slice(0, 10);
		const stem = (this.data.exportFilenameStem || 'remit').replace(/[^a-zA-Z0-9_-]/g, '_');
		return `${stem}_${supplier}_${date}`;
	}

	canExport(): boolean {
		return !this.loading && !this.exporting && this.results.some((r) => !!r.res?.html);
	}

	exportPdf(): void {
		const ctx = this.currentExportContext();
		if (!ctx) { return; }
		this.exporting = true;
		const encoded: string = this.globalSearchService.base64encode(ctx.html);
		const data = {
			content: encoded,
			filename: `${this.exportFilenameStem()}.pdf`,
			landscape: !!this.data.exportLandscape,
			title: this.data.suppressPrintHeader ? '' : ctx.subject,
			subtitle: this.data.suppressPrintHeader ? '' : ('Created ' + new Date().toLocaleString()),
		};
		this.printService.pdf(data).subscribe(
			(result: any) => {
				this.globalSearchService.downloadPdf(result.content, data.filename);
				this.exporting = false;
			},
			() => {
				this.globalSearchService.showNotification('Failed to generate PDF', 'danger', 'bottom', 'right');
				this.exporting = false;
			},
		);
	}

	exportXls(): void {
		const ctx = this.currentExportContext();
		if (!ctx) { return; }
		this.exporting = true;
		const encoded: string = this.globalSearchService.base64encode(ctx.html);
		const data = {
			content: encoded,
			filename: `${this.exportFilenameStem()}.xlsx`,
			title: ctx.subject,
			subtitle: 'Created ' + new Date().toLocaleString(),
		};
		this.printService.xls(data).subscribe(
			(result: any) => {
				this.globalSearchService.downloadXls(result.content, data.filename);
				this.exporting = false;
			},
			() => {
				this.globalSearchService.showNotification('Failed to generate spreadsheet', 'danger', 'bottom', 'right');
				this.exporting = false;
			},
		);
	}

	onTabChange(index: number): void {
		this.activeTabIndex = index;
	}

	close(): void {
		this.dialogRef.close();
	}
}
